import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../lib/axios'

/** The address a bookmark keeps: path and query, never the hash. */
export function pageUrl(fullPath) {
    return String(fullPath ?? '').split('#')[0]
}

const reason = (e, fallback) => e?.response?.data?.message || fallback

/**
 * The top bar's bookmarks. Optimistic throughout: the list changes at once, the request
 * follows, and a refusal puts the list back and says why.
 */
export const useBookmarksStore = defineStore('bookmarks', () => {
    const items = ref([])
    const loaded = ref(false)
    const error = ref('')
    let owner = null   // whose list this is
    let tmp = 0

    /**
     * Load the list for this person. A session can expire and someone else sign in on the
     * same tab without logout ever running, so the store remembers whose list it holds and
     * starts again for anyone else - and drops a reply that arrives after the switch.
     */
    async function load(userId) {
        if (owner === userId && loaded.value) return
        if (owner !== userId) reset()
        owner = userId
        try {
            const { data } = await api.get('/bookmarks')
            if (owner !== userId) return
            items.value = data.data ?? []
            loaded.value = true
        } catch (e) {
            if (owner === userId) error.value = reason(e, 'Bookmarks could not be loaded.')
        }
    }

    const find = (url) => items.value.find(b => b.url === url)

    async function add({ url, label, context = null }) {
        if (find(url)) return
        error.value = ''
        const temp = { id: `tmp-${++tmp}`, url, label, context, pending: true }
        items.value = [...items.value, temp]
        try {
            const { data } = await api.post('/bookmarks', { url, label, context })
            const saved = data.data
            items.value = items.value.filter(b => b.id !== saved.id).map(b => (b.id === temp.id ? saved : b))
        } catch (e) {
            items.value = items.value.filter(b => b.id !== temp.id)
            error.value = reason(e, 'That page could not be bookmarked.')
        }
    }

    async function rename(id, label) {
        const row = items.value.find(b => b.id === id)
        const clean = (label ?? '').trim()
        if (!row || !clean || clean === row.label) return
        error.value = ''
        const before = row.label
        row.label = clean
        try {
            await api.patch(`/bookmarks/${id}`, { label: clean })
        } catch (e) {
            row.label = before
            error.value = reason(e, 'That bookmark could not be renamed.')
        }
    }

    async function remove(id) {
        const before = items.value
        if (!before.some(b => b.id === id)) return
        error.value = ''
        items.value = before.filter(b => b.id !== id)
        try {
            await api.delete(`/bookmarks/${id}`)
        } catch (e) {
            items.value = before
            error.value = reason(e, 'That bookmark could not be removed.')
        }
    }

    async function reorder(list) {
        const before = items.value
        error.value = ''
        items.value = list
        const ids = list.filter(b => !b.pending).map(b => b.id)
        try {
            await api.post('/bookmarks/reorder', { ids })
        } catch (e) {
            items.value = before
            error.value = reason(e, 'The new order could not be saved.')
        }
    }

    function reset() {
        items.value = []
        loaded.value = false
        error.value = ''
        owner = null
    }

    return { items, loaded, error, load, find, add, rename, remove, reorder, reset }
})
